// Contient les fonctions correspondant a chaque requete fonctionnelle,
// renvoyant des datas formattées propres à chaque requete.
// De manière commune, récupère les token d'accès, requête leur mise à jour si besoin, et formatte les retours d'erreur connus

import LoggerService,{formatTrace,formatInfo} from '../LoggerService';
import I_RESET_PWD from "./interfaces/I_RESET_PWD";

import {
  _checkTokens,
  _checkErrors,
  _tryOrRefresh,
} from './services/_base';
import {
  getHelloResponse,
  getLoginResponse,
  getUsersMeResponse,
  getLogoutResponse, postResetPassword, postForgotPassword,
} from './ApiEndpoints';
import I_HELLO from './interfaces/I_HELLO';
import I_LOGIN from './interfaces/I_LOGIN';
import I_LOGOUT from './interfaces/I_LOGOUT';
import I_USERS_ME from './interfaces/I_USERS_ME';

import activityTypesServices from './services/activityTypes';
import taskTypesServices from './services/taskTypes';
import skillsServices from './services/skills';
import billingCustomersServices from './services/billingCustomers';
import usersServices from './services/users';
import collaboratorsServices from './services/collaborators';
import ooodaysServices from './services/ooodays';
import customersServices from './services/customers';
import chargesServices from './services/charges';
import activitiesServices from './services/activities';
import chargesModelsServices from './services/charges-models';
import planificationServices from './services/planification';
import logsServices from './services/logs';
import filtersServices from './services/filters';
import chargesReportServices from './services/charges-report';
import alertsServices from './services/alerts';
import microsoftServices from './services/microsoft';
import organisationsServices from './services/organisations';

const logger = LoggerService.getInstance();

/**
 * Récupère les configurations du service Osac Planif gérées par le backend
 * @returns {Promise<I_HELLO>}
*/
export const getHello = async ():Promise<I_HELLO> => {
  logger.debug('ApiServices - getHello...');
  // requete le endpoint
  const response:Response = await getHelloResponse();
  logger.trace(...formatTrace('ApiServices - getHello - returned response',response));
  // verifie que la reponse est ok, sinon throw error
  await _checkErrors(response);
  // extrait les données
  const body:I_HELLO = await response.json();
  logger.debug('ApiServices - getHello - ok, returned body',body);
  return body;
};


interface I_LOGIN_PARAMS {
  email: string,
  password: string,
}
/**
 * Récupère les token du user à partir des login / mdp
 * @param {I_LOGIN_PARAMS} obj
 * @returns {Promise<I_LOGIN>}
 */
export const getUserTokens = async ({email,password}:I_LOGIN_PARAMS):Promise<I_LOGIN> => {
  logger.debug('ApiServices - getUserTokens...');
  // requete le endpoint
  const response = await getLoginResponse({email,password});
  logger.trace(...formatTrace('ApiServices - getUserTokens - returned response',response));
  // verifie que la reponse est ok, sinon throw error
  await _checkErrors(response);
  // extrait les données
  const body:I_LOGIN = await response.json();
  logger.debug('ApiServices - getUserTokens - ok, returned body',body);
  return body;
};

interface I_LOGOUT_PARAMS {
  accessToken: string,
  refreshToken: string,
  dispatch: any; // on a besoin du dispatch pour store les tokens en cas de refresh
  state: any; // on a besoin du state pour comparer les tokens en cas de 401
}
/**
 * Invalide les token sur le backend
 * @param obj
 * @returns
 */
export const logout = async ({accessToken,refreshToken,dispatch,state}:I_LOGOUT_PARAMS):Promise<I_LOGOUT> => {
  logger.debug('ApiServices - logout...');
  // requete le endpoint
  const response = await _tryOrRefresh({
    request: (usedAccessToken:string) => {
      return getLogoutResponse({accessToken:usedAccessToken});
    },
    accessToken,
    refreshToken,
    dispatch,state
  });
  logger.trace(...formatTrace('ApiServices - logout - returned response',response));
  // verifie que la reponse est ok, sinon throw error
  await _checkErrors(response);
  // extrait les données
  const body:I_LOGOUT = await response.json();
  logger.debug('ApiServices - logout - ok, returned body',body);
  logger.info(...formatInfo('Loggued out!'));
  return body;
};


interface I_USERME_PARAMS {
  accessToken: string,
  refreshToken: string,
  dispatch: any; // on a besoin du dispatch pour store les tokens en cas de refresh
  state: any; // on a besoin du state pour comparer les tokens en cas de 401
}
/**
 * Récupère les infos du user (nom, mail...)
 * @param obj
 * @returns {Promise<I_USERS_ME>}
 */
export const getUsersMe = async ({accessToken,refreshToken,dispatch,state}:I_USERME_PARAMS):Promise<I_USERS_ME> => {
  logger.debug('ApiServices - getUsersMe...');
  // pre-verification qu'on a bien des token, sinon sert a rien de faire la requete
  await _checkTokens(accessToken,refreshToken);
  // requete le endpoint
  const response = await _tryOrRefresh({
    request: (usedAccessToken:string) => {
      return getUsersMeResponse({accessToken:usedAccessToken});
    },
    accessToken,
    refreshToken,
    dispatch,state
  });
  logger.trace(...formatTrace('ApiServices - getUsersMe - returned response',response));
  // verifie que la reponse est ok, sinon throw error
  await _checkErrors(response);
  // extrait les données
  const body:I_USERS_ME = await response.json();
  logger.debug('ApiServices - getUsersMe - ok, returned body',body);
  return body;
};


interface I_FORGOT_PWD_PARAMS {
  email: string
}
/**
 * Envoie un email de réinitialisation du mot de passe
 * @param email
 */
export const sendForgotPassword = async ({email}: I_FORGOT_PWD_PARAMS) => {
  logger.trace('ApiServices - sendForgotPassword...');
  // requete le endpoint
  const response = await postForgotPassword({email});
  logger.trace(...formatTrace('ApiServices - sendForgotPassword - returned response',response));
  // verifie que la reponse est ok, sinon throw error
  await _checkErrors(response);
  // extrait les données
  const body:I_RESET_PWD = await response.json();
  logger.trace('ApiServices - sendForgotPassword - ok, returned body',body);
  return body;
}

interface I_RESET_PWD_PARAMS {
  password: string
  token: string
}
/**
 * Enregistre le nouveau mot de passe
 * @param password
 * @param token
 */
export const sendResetPassword = async ({password, token}: I_RESET_PWD_PARAMS) => {
  logger.trace('ApiServices - postNewPassword...');
  // requete le endpoint
  const response = await postResetPassword({password, token})
  logger.trace(...formatTrace('ApiServices - postNewPassword - returned response',response));

  // verifie que la reponse est ok, sinon throw error
  await _checkErrors(response);
  // extrait les données
  const body:I_RESET_PWD = await response.json();
  logger.trace('ApiServices - postNewPassword - ok, returned body',body);
  return body;
}



// ---------------------------------
// --- ACTIVITY TYPES
// ---------------------------------

export const getActivityTypes = activityTypesServices.getList;
export const createActivityType = activityTypesServices.create;
export const deleteActivityType = activityTypesServices.remove;
export const updateActivityType = activityTypesServices.update;

// ---------------------------------
// --- TASK TYPES
// ---------------------------------

export const getTaskTypes = taskTypesServices.getList;
export const createTaskType = taskTypesServices.create;
export const deleteTaskType = taskTypesServices.remove;
export const updateTaskType = taskTypesServices.update;

// ---------------------------------
// --- SKILLS
// ---------------------------------

export const getSkills = skillsServices.getList;
export const createSkill = skillsServices.create;
export const deleteSkill = skillsServices.remove;
export const updateSkill = skillsServices.update;

// ---------------------------------
// --- BILLING CUSTOMERS
// ---------------------------------

export const getBillingCustomers = billingCustomersServices.getList;
export const createBillingCustomer = billingCustomersServices.create;
export const deleteBillingCustomer = billingCustomersServices.remove;
export const updateBillingCustomer = billingCustomersServices.update;
export const importBillingCustomers = billingCustomersServices.importList;

// ---------------------------------
// --- USERS
// ---------------------------------

export const getUsers = usersServices.getList;
export const createUser = usersServices.create;
export const deleteUser = usersServices.remove;
export const updateUser = usersServices.update;
export const getUser = usersServices.getItem;

// ---------------------------------
// --- COLLABORATEURS
// ---------------------------------

export const getCollaborators = collaboratorsServices.getList;
export const createCollaborator = collaboratorsServices.create;
export const deleteCollaborator = collaboratorsServices.remove;
export const updateCollaborator = collaboratorsServices.update;
export const getCollaborator = collaboratorsServices.getItem;
export const importCollaborators = collaboratorsServices.importList;

// ---------------------------------
// --- OOODAYS collabs
// ---------------------------------

export const getOOODays = ooodaysServices.getList;
export const updateOOODay = ooodaysServices.update;

// ---------------------------------
// --- ("AUDITED") CUSTOMERS
// ---------------------------------

export const getCustomers = customersServices.getList;
export const createCustomer = customersServices.create;
export const deleteCustomer = customersServices.remove;
export const updateCustomer = customersServices.update;
export const getCustomer = customersServices.getItem;
export const importCustomers = customersServices.importList;

// ---------------------------------
// --- CHARGES
// ---------------------------------

export const getCharges = chargesServices.getList;
export const createCharge = chargesServices.create;
export const deleteCharge = chargesServices.remove;
export const updateCharge = chargesServices.update;
export const getCharge = chargesServices.getItem;
export const importCharges = chargesServices.importList;
export const exportCharges = chargesServices.exportList;

// ---------------------------------
// --- ACTIVITIES
// ---------------------------------

export const getActivities = activitiesServices.getList;
export const createActivity = activitiesServices.create;
export const deleteActivity = activitiesServices.remove;
export const updateActivity = activitiesServices.update;
export const getActivity = activitiesServices.getItem;
export const exportActivities = activitiesServices.exportList;

// ---------------------------------
// --- CHARGES MODELS
// ---------------------------------

export const getChargesModels = chargesModelsServices.getList;
export const createChargeModel = chargesModelsServices.create;
export const deleteChargeModel = chargesModelsServices.remove;
export const updateChargeModel = chargesModelsServices.update;
export const getChargeModel = chargesModelsServices.getItem;

// ---------------------------------
// --- PLANIFICATION
// ---------------------------------

export const getPlanificationTotals = planificationServices.getTotals;
export const getPlanificationByCollaborators = planificationServices.getByCollaborators;
export const getPlanificationByCustomers = planificationServices.getByCustomers;
export const getPlanificationByActivityTypes = planificationServices.getByActivityTypes;
export const getPlanificationBySkills = planificationServices.getBySkills;
export const getPlanificationByFunctions = planificationServices.getByFunctions;
export const getPlanificationByOrganisations = planificationServices.getByOrganisations;

// ---------------------------------
// --- LOGS
// ---------------------------------

export const getLogsCharge = logsServices.getLogsCharge;
export const getLogsActivity = logsServices.getLogsActivity;
export const getLogsCollaborator = logsServices.getLogsCollaborator;
export const getLogsCustomer = logsServices.getLogsCustomer;

// ---------------------------------
// --- FILTERS
// ---------------------------------

export const getFilters = filtersServices.getList;
export const createFilter = filtersServices.create;
export const deleteFilter = filtersServices.remove;
export const updateFilter = filtersServices.update;

// ---------------------------------
// --- CHARGES REPORT
// ---------------------------------

export const getChargesWithoutPagination = chargesReportServices.getChargesList;
export const createReport = chargesReportServices.create;
export const getReports = chargesReportServices.getList;
export const cancelReports = chargesReportServices.cancel;
export const validateReports = chargesReportServices.validate;

// ---------------------------------
// --- ALERTS
// ---------------------------------

export const getAlerts = alertsServices.getList;
export const getAlertsPreferences = alertsServices.getPreferences;
export const setAlertsPreferences = alertsServices.setPreferences;
export const acknowledgeAlert = alertsServices.acknowledge;

// ---------------------------------
// --- MICROSOFT
// ---------------------------------

export const getMicrosoftLink = microsoftServices.getLink;
export const connectMicrosoft = microsoftServices.connect;

// ---------------------------------
// --- ORGANISATIONS
// ---------------------------------

export const getOrganisationsSets = organisationsServices.getList;
